/**
 * Screen 1d — the creator dashboard.
 *
 * Two axes, kept apart. The tag row is **state** (All / Live / Draft /
 * Archived); the pill on each row is **visibility** (Private · Invite only ·
 * Unlisted · Listed). The original brief conflated them, and the result was a
 * course that could be "Draft" and "Unlisted" at once with no way to say so.
 *
 * There is no **New course** here. A course arrives from Compose, so the
 * primary action opens a dialog that says how — the design critic's decision,
 * and the alternative was a form that creates a shell nothing can fill.
 */

import { useMemo, useState } from 'react';
import type { CourseState, TeachCourseRowWire, Visibility } from '@wire';
import { teach } from '../api';
import { AssignDialog } from '../components/AssignDialog';
import { CourseMark, Menu, StatRow, StateBadge, VisibilitySelect } from '../components/Bits';
import { Empty, ErrorNote, Loading, OkNote, PageTitle, Panel } from '../components/Chrome';
import { NewBundleDialog } from '../components/NewBundleDialog';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { HAIR, figure, formatDeadline, meta, monoPlain, plural } from '../theme';
import { useActions, useLoad } from '../useLoad';

type Tag = 'all' | CourseState;

const TAGS: { value: Tag; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' }
];

/**
 * "3 days ago", for the dashboard's own subline.
 *
 * `formatWhen` (shared with the sibling apps) gives a weekday name once a date
 * is inside a week — right for a notification, wrong for this one line, which
 * the design writes as a literal day count. Local to this file rather than a
 * second mode on the shared helper, which every other caller would have to
 * keep ignoring.
 */
function daysAgo(unixSeconds: number): string {
  const days = Math.floor((Date.now() / 1000 - unixSeconds) / 86400);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

export default function TeachCourses() {
  const { Button, Dialog, Stat } = ds();
  const { navigate } = useRouter();
  const { data, error, loading, reload } = useLoad(
    () => teach.courses(),
    'Could not load your courses.'
  );
  const { busy, error: wrote, ok, act, setOk } = useActions(reload);

  const [tag, setTag] = useState<Tag>('all');
  const [uploading, setUploading] = useState(false);
  const [newBundle, setNewBundle] = useState(false);
  const [assign, setAssign] = useState<TeachCourseRowWire | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TeachCourseRowWire | null>(null);

  const rows = data?.courses ?? [];
  const counts = useMemo(() => {
    const by = { all: rows.length, live: 0, draft: 0, archived: 0 };
    for (const row of rows) by[row.card.state] += 1;
    return by;
  }, [rows]);

  const shown = tag === 'all' ? rows : rows.filter((r) => r.card.state === tag);
  const lastUpload = rows.reduce<number | null>(
    (newest, row) =>
      row.lastUploadAt !== null && (newest === null || row.lastUploadAt > newest)
        ? row.lastUploadAt
        : newest,
    null
  );

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  return (
    <>
      <PageTitle
        title="Courses."
        meta={meta(
          `${data.stats.live} live`,
          lastUpload === null ? null : `last upload ${daysAgo(lastUpload)}`
        )}
      >
        <Button variant="secondary" size="sm" onClick={() => setNewBundle(true)}>
          New bundle
        </Button>
        <Button variant="primary" size="sm" onClick={() => setUploading(true)}>
          Upload from Compose
        </Button>
      </PageTitle>

      <ErrorNote>{wrote}</ErrorNote>
      <OkNote>{ok}</OkNote>

      <Panel>
        <StatRow>
          <Stat label="Live courses" value={data.stats.live.toLocaleString()} />
          <Stat label="Learners this month" value={data.stats.learnersThisMonth.toLocaleString()} />
          <Stat
            label="Completed"
            value={data.stats.completedPct === null ? '—' : `${data.stats.completedPct}%`}
          />
          <Stat label="Past deadline" value={data.stats.pastDeadline.toLocaleString()} />
        </StatRow>
      </Panel>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TAGS.map((t) => (
          <Button
            key={t.value}
            variant={tag === t.value ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setTag(t.value)}
          >
            {`${t.label} ${counts[t.value]}`}
          </Button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Empty line={tag === 'all' ? 'Nothing here yet.' : `No ${tag} course.`}>
          {tag === 'all' && (
            <Button variant="secondary" size="sm" onClick={() => setUploading(true)}>
              Upload from Compose
            </Button>
          )}
        </Empty>
      ) : (
        <Panel style={{ display: 'grid', gap: 0 }}>
          {shown.map((row) => (
            <Row
              key={row.card.id}
              row={row}
              busy={busy}
              onVisibility={(visibility) =>
                void act(
                  () => teach.updateCourse(row.card.id, { visibility }),
                  `${row.card.title} is now ${visibility === 'assigned' ? 'invite only' : visibility}.`
                )
              }
              onAssign={() => setAssign(row)}
              onDelete={() => setConfirmDelete(row)}
              onPublish={() =>
                void act(() => teach.publish(row.card.id), `${row.card.title} is live.`)
              }
              onUnpublish={() =>
                void act(() => teach.unpublish(row.card.id), `${row.card.title} is no longer live.`)
              }
            />
          ))}
        </Panel>
      )}

      {uploading && (
        <Dialog
          open
          position="fixed"
          width={440}
          title="Upload from Compose"
          description="Open the course in Compose, then choose Course → Upload to Learn."
          onClose={() => setUploading(false)}
          footer={
            <Button variant="primary" size="md" onClick={() => setUploading(false)}>
              Done
            </Button>
          }
        />
      )}

      {newBundle && (
        <NewBundleDialog
          onClose={() => setNewBundle(false)}
          onCreated={(id) => navigate(`/teach/bundles/${id}`)}
        />
      )}

      {assign && (
        <AssignDialog
          courseId={assign.card.id}
          subject={assign.card.title}
          onClose={() => setAssign(null)}
          onDone={(result) => {
            setAssign(null);
            setOk(
              `Assigned to ${plural(result.assigned, 'person', 'people')}.${
                result.alreadyAssigned > 0 ? ` ${result.alreadyAssigned} already had it.` : ''
              }`
            );
            // The row's learner and deadline counts have just moved.
            void reload();
          }}
        />
      )}

      {confirmDelete && (
        <Dialog
          open
          position="fixed"
          width={440}
          title={`Delete ${confirmDelete.card.title}?`}
          description="Its files, progress, feedback and assignments go with it."
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <Button variant="ghost" size="md" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                disabled={busy}
                onClick={() => {
                  const row = confirmDelete;
                  setConfirmDelete(null);
                  void act(() => teach.deleteCourse(row.card.id), `${row.card.title} is deleted.`);
                }}
              >
                Delete
              </Button>
            </>
          }
        />
      )}

    </>
  );
}

function Row({
  row,
  busy,
  onVisibility,
  onAssign,
  onDelete,
  onPublish,
  onUnpublish
}: {
  row: TeachCourseRowWire;
  busy: boolean;
  onVisibility: (next: Visibility) => void;
  onAssign: () => void;
  onDelete: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
}) {
  const { Badge, Button } = ds();
  const { navigate } = useRouter();
  const { card } = row;

  return (
    <div
      className="mv-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 4px',
        borderTop: HAIR,
        flexWrap: 'wrap'
      }}
    >
      <CourseMark colour={card.colour} />

      <div style={{ flex: 1, minWidth: 180, display: 'grid', gap: 3 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link to={`/course/${card.id}`}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
              {card.title}
            </span>
          </Link>
          {card.state !== 'live' && <StateBadge state={card.state} />}
        </span>
        <span style={monoPlain}>{meta(card.code, row.address)}</span>
      </div>

      <VisibilitySelect value={card.visibility} disabled={busy} onChange={onVisibility} />

      <span style={{ ...monoPlain, ...figure, minWidth: 110 }}>
        {`${row.learners} · ${row.completed} done`}
      </span>

      {/* A teacher's deadline column is a date, not a learner's badge: this row
          covers many people, and "Overdue by 2 days" is true of some of them. */}
      <span style={{ display: 'grid', gap: 3, minWidth: 118 }}>
        <span style={monoPlain}>
          {row.dueAt === null ? 'No deadline' : `Due ${formatDeadline(row.dueAt)}`}
        </span>
        {row.pastDue > 0 && (
          <Badge tone="danger" mono>{`${row.pastDue} past deadline`}</Badge>
        )}
      </span>

      <Button variant="secondary" size="sm" onClick={onAssign}>
        Assign
      </Button>

      {/* The one action a draft (or an archived course) exists for is a
          control on the row, not a third click inside a menu. Every other
          state change stays there. */}
      {card.state !== 'live' && (
        <Button variant="primary" size="sm" onClick={onPublish}>
          Publish
        </Button>
      )}

      <Menu
        label={`More for ${card.title}`}
        items={[
          {
            label: 'Report',
            onSelect: () => navigate(`/teach/courses/${card.id}/report`)
          },
          {
            label: 'Feedback form',
            onSelect: () => navigate(`/teach/courses/${card.id}/feedback`)
          },
          ...(card.state === 'live'
            ? [{ label: 'Unpublish', onSelect: onUnpublish }]
            : []),
          { label: 'Delete', onSelect: onDelete, danger: true }
        ]}
      />
    </div>
  );
}
